import { useState, useEffect } from 'react';
import { api } from '../api/client';

export function AdminUsersPage() {
  const [users, setUsers] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [newUser, setNewUser] = useState({ id: '', username: '', password: '', display_name: '', email: '', role: 'user' });
  const [editingId, setEditingId] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.adminGetUsers().catch(() => ({ users: [] })),
      api.adminGetPlans().catch(() => ({ plans: [] }))
    ]).then(([u, p]) => {
      setUsers(u.users || []);
      setPlans(p.plans || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleAssignPlan = async (userId: string, planId: string) => {
    if (!planId) return;
    if (!confirm('Are you sure you want to assign this plan and credit tokens?')) return;
    try {
      await api.adminAssignPlan(userId, planId);
      load();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.adminUpdateUser(editingId, newUser);
      } else {
        await api.adminCreateUser(newUser);
      }
      setNewUser({ id: '', username: '', password: '', display_name: '', email: '', role: 'user' });
      setEditingId(null);
      load();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleDeleteUser = async (id: string) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.adminDeleteUser(id);
      load();
    } catch (err: any) {
      alert(err.message);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">Users</h1>
          <p className="page-subtitle">Manage users and quotas</p>
        </div>
      </div>

      <div className="card" style={{ background: 'rgba(34,211,238,0.05)', border: '1px solid rgba(34,211,238,0.2)' }}>
        <h2 style={{ fontSize: '13px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>🔌</span> API Integration (Billing & Payments)
        </h2>
        <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px', lineHeight: 1.5 }}>
          You can automate user creation and plan assignments from your billing system (e.g. WHMCS, Stripe, Hostinger) by calling the Admin API. 
          Use an API Key generated from the <strong>API Keys</strong> tab in the <code style={{ color: 'var(--accent-primary)' }}>Authorization: Bearer sk-eri-...</code> header.
        </p>
        <div style={{ background: 'var(--bg-base)', padding: '12px', borderRadius: '6px', fontSize: '11px', fontFamily: 'monospace', color: 'var(--text-secondary)', overflowX: 'auto', whiteSpace: 'pre' }}>
          <span style={{ color: '#a78bfa' }}>POST</span> {import.meta.env.VITE_API_BASE ?? window.location.origin}/api/admin/users
          <br/><br/>
          {`{
  "username": "newuser123",
  "password": "securepassword",
  "email": "user@example.com",
  "display_name": "New User"
}`}
        </div>
      </div>

      <div className="card">
        <h2 style={{ fontSize: '14px', marginBottom: '16px' }}>{editingId ? 'Edit User' : 'Create User'}</h2>
        <form onSubmit={handleSaveUser} style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {!editingId && (
            <>
              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>Username</label>
                <input className="input" value={newUser.username} onChange={e => setNewUser({...newUser, username: e.target.value})} required />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>Password</label>
                <input type="password" className="input" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} required={!editingId} />
              </div>
            </>
          )}
          <div>
            <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>Display Name</label>
            <input className="input" value={newUser.display_name} onChange={e => setNewUser({...newUser, display_name: e.target.value})} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>Email</label>
            <input type="email" className="input" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px' }}>Role</label>
            <select className="input" value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value})}>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button className="btn btn--primary">{editingId ? 'Update' : 'Create'}</button>
          {editingId && (
            <button type="button" className="btn btn--secondary" onClick={() => { setEditingId(null); setNewUser({ id: '', username: '', password: '', display_name: '', email: '', role: 'user' }); }}>Cancel</button>
          )}
        </form>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ background: 'var(--bg-elevated)', borderBottom: '1px solid var(--border-color)', textAlign: 'left' }}>
              <th style={{ padding: '12px 16px', fontWeight: 600 }}>User</th>
              <th style={{ padding: '12px 16px', fontWeight: 600 }}>Role</th>
              <th style={{ padding: '12px 16px', fontWeight: 600 }}>Current Plan</th>
              <th style={{ padding: '12px 16px', fontWeight: 600 }}>Token Balance</th>
              <th style={{ padding: '12px 16px', fontWeight: 600 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={5} style={{ padding: '16px', textAlign: 'center' }}>Loading...</td></tr> :
              users.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <div style={{ fontWeight: 600 }}>{u.username}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{u.display_name || u.external_id} • {u.email}</div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span className={`badge ${u.role === 'admin' ? 'badge--purple' : 'badge--blue'}`}>{u.role}</span>
                  </td>
                  <td style={{ padding: '12px 16px', fontWeight: 500 }}>
                    {u.plan} 
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      (Max Cores: {plans.find(p => p.id === u.plan)?.max_cores || 1})
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', fontFamily: 'monospace' }}>{Number(u.tokens_balance).toLocaleString()}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <select 
                      className="input" 
                      style={{ padding: '4px 8px', fontSize: '12px', width: 'auto', display: 'inline-block' }}
                      value=""
                      onChange={(e) => handleAssignPlan(u.id, e.target.value)}
                    >
                      <option value="" disabled>Assign Plan (Add Credit)</option>
                      {plans.map(p => (
                        <option key={p.id} value={p.id}>{p.name} (+{Number(p.token_credit).toLocaleString()} tok, {p.max_cores || 1} cores)</option>
                      ))}
                    </select>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                      <button className="btn btn--secondary btn--sm" onClick={() => { setEditingId(u.id); setNewUser({ ...u, password: '' }); }}>Edit</button>
                      <button className="btn btn--secondary btn--sm" style={{ color: '#ef4444', borderColor: '#ef4444' }} onClick={() => handleDeleteUser(u.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
    </div>
  );
}
